// src/components/Login/Onboarding.jsx
// 🔥 V2 ENTERPRISE — Flujo de pago estilo Netflix/Spotify/Notion
// Redirige a /planos al finalizar para prueba gratuita de 7 días

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles,
  Building2,
  Users,
  Home,
  Calendar,
  FileText,
  BarChart3,
  Bell,
  Moon,
  Sun,
  Palette,
  Check,
  ArrowRight,
  ArrowLeft,
  Rocket,
  Shield,
  Zap,
  Globe,
  Smartphone,
  Cloud,
  Award,
  CheckCircle,
  Settings,
  Briefcase,
  Target,
  TrendingUp,
  CreditCard
} from 'lucide-react';

// ============================================
// 🎨 CONSTANTES E CONFIGURAÇÕES
// ============================================

const STEPS = [
  { 
    id: 1, 
    name: 'Boas-vindas', 
    icon: Sparkles,
    description: 'Configure sua experiência'
  },
  { 
    id: 2, 
    name: 'Preferências', 
    icon: Settings,
    description: 'Personalize sua conta'
  },
  { 
    id: 3, 
    name: 'Módulos', 
    icon: Briefcase,
    description: 'Ative as funcionalidades'
  },
  { 
    id: 4, 
    name: 'Plano', 
    icon: CreditCard,
    description: 'Escolha seu plano'
  }
];

const THEMES = [
  { 
    id: 'light', 
    name: 'Claro', 
    icon: Sun, 
    color: 'from-amber-400 to-yellow-500',
    bgColor: 'bg-amber-50',
    description: 'Tema claro clássico'
  },
  { 
    id: 'dark', 
    name: 'Escuro', 
    icon: Moon, 
    color: 'from-indigo-500 to-purple-600',
    bgColor: 'bg-indigo-50',
    description: 'Fácil para os olhos'
  },
  { 
    id: 'auto', 
    name: 'Automático', 
    icon: Globe, 
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-50',
    description: 'Segue o sistema'
  }
];

const MODULES = [
  {
    id: 'clients',
    name: 'Clientes',
    icon: Users,
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-50',
    description: 'Gestão completa de clientes',
    features: ['Pipeline de vendas', 'Histórico', 'Score automático']
  },
  {
    id: 'properties',
    name: 'Imóveis',
    icon: Home,
    color: 'from-emerald-500 to-green-500',
    bgColor: 'bg-emerald-50',
    description: 'Catálogo de propriedades',
    features: ['Fotos ilimitadas', 'Status automático', 'Destaques']
  },
  {
    id: 'finance',
    name: 'Financeiro',
    icon: TrendingUp,
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-50',
    description: 'Controle financeiro',
    features: ['Receitas/Despesas', 'Relatórios', 'Metas']
  },
  {
    id: 'tasks',
    name: 'Tarefas',
    icon: Target,
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-50',
    description: 'Organização diária',
    features: ['Prioridades', 'Prazos', 'Atribuições']
  },
  {
    id: 'calendar',
    name: 'Calendário',
    icon: Calendar,
    color: 'from-indigo-500 to-blue-500',
    bgColor: 'bg-indigo-50',
    description: 'Agenda integrada',
    features: ['Eventos', 'Lembretes', 'Sincronização']
  },
  {
    id: 'documents',
    name: 'Documentos',
    icon: FileText,
    color: 'from-amber-500 to-yellow-500',
    bgColor: 'bg-amber-50',
    description: 'Arquivos e contratos',
    features: ['IndexedDB', 'Offline-first', 'Organização']
  }
];

const FEATURES = [
  {
    icon: Zap,
    title: 'Offline First',
    description: 'Funciona sem internet',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50'
  },
  {
    icon: Shield,
    title: 'Multi-tenant',
    description: 'Dados isolados por usuário',
    color: 'text-blue-500',
    bgColor: 'bg-blue-50'
  },
  {
    icon: Globe,
    title: 'White Label',
    description: 'Personalize com sua marca',
    color: 'text-purple-500',
    bgColor: 'bg-purple-50'
  },
  {
    icon: Smartphone,
    title: 'Responsivo',
    description: 'Funciona em qualquer tela',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50'
  },
  {
    icon: Cloud,
    title: 'Auto-backup',
    description: 'Seus dados sempre seguros',
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-50'
  },
  {
    icon: Award,
    title: 'Premium',
    description: 'Experiência profissional',
    color: 'text-amber-500',
    bgColor: 'bg-amber-50'
  }
];

const QUICK_ACTIONS = [
  {
    id: 'import',
    name: 'Importar dados',
    description: 'Traga seus clientes e imóveis',
    icon: Cloud
  },
  {
    id: 'tour',
    name: 'Fazer tour',
    description: 'Conheça todas as funcionalidades',
    icon: Rocket
  },
  {
    id: 'config',
    name: 'Configurar empresa',
    description: 'White label e personalização',
    icon: Settings
  }
];

// ============================================
// 🎨 COMPONENTE PRINCIPAL
// ============================================

const Onboarding = ({ 
  user = { name: 'Usuário' },
  onComplete,
  onSkip,
  companyId = 'default'
}) => {
  const navigate = useNavigate();
  
  // ============================================
  // 📊 ESTADOS
  // ============================================
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [preferences, setPreferences] = useState({
    theme: 'auto',
    modules: ['clients', 'properties'],
    notifications: true,
    quickActions: []
  });
  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  // ============================================
  // 🎯 HANDLERS
  // ============================================
  const handleNext = () => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    setCurrentStep(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleModuleToggle = (moduleId) => {
    setPreferences(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleId)
        ? prev.modules.filter(id => id !== moduleId)
        : [...prev.modules, moduleId]
    }));
  };

  const handleQuickAction = (actionId) => {
    setPreferences(prev => ({
      ...prev,
      quickActions: prev.quickActions.includes(actionId)
        ? prev.quickActions.filter(id => id !== actionId)
        : [...prev.quickActions, actionId]
    }));
  };

  // 🔥 V2: Redirige a /planos para elegir plan con 7 días gratis
  const handleComplete = async () => {
    setLoading(true);
    setShowConfetti(true);
    
    try {
      // Guardar preferencias en localStorage
      localStorage.setItem(`onboarding_${companyId}`, JSON.stringify({
        ...preferences,
        completedAt: new Date().toISOString(),
        completed: true
      }));
      
      // Disparar evento
      window.dispatchEvent(new CustomEvent('auth:onboarding:completed', {
        detail: { preferences, userId: user?.id }
      }));
      
      // Callback
      if (onComplete) {
        onComplete(preferences);
      }
      
      // 🔥 V2: Redirigir a /planos (flujo estilo Netflix/Spotify)
      setTimeout(() => {
        navigate('/planos?source=onboarding');
      }, 1500);
      
    } catch (error) {
      console.error('❌ Erro no onboarding:', error);
      setLoading(false);
    }
  };

  // ============================================
  // 🎨 RENDER DOS PASSOS
  // ============================================

  const renderStep1 = () => (
    <motion.div
      key="step1"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      {/* Header de boas-vindas */}
      <div className="text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-xl"
        >
          <Sparkles className="w-10 h-10 text-white" />
        </motion.div>
        
        <motion.h2 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-slate-900 mb-2"
        >
          Bem-vindo, {user?.name || 'Usuário'}! 👋
        </motion.h2>
        
        <motion.p 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-slate-600"
        >
          Vamos configurar sua experiência no PropZyn
        </motion.p>
      </div>

      {/* Grid de features */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-2 md:grid-cols-3 gap-4"
      >
        {FEATURES.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 * index }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="p-4 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all"
            >
              <div className={`${feature.bgColor} w-10 h-10 rounded-lg flex items-center justify-center mb-3`}>
                <Icon className={`w-5 h-5 ${feature.color}`} />
              </div>
              <h3 className="font-medium text-slate-900 text-sm mb-1">
                {feature.title}
              </h3>
              <p className="text-xs text-slate-500">
                {feature.description}
              </p>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Ações rápidas */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="bg-blue-50 rounded-xl p-4"
      >
        <p className="text-sm font-medium text-blue-900 mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4" />
          Ações rápidas
        </p>
        <div className="flex flex-wrap gap-2">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            const isSelected = preferences.quickActions.includes(action.id);
            
            return (
              <motion.button
                key={action.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleQuickAction(action.id)}
                className={`
                  px-3 py-2 rounded-lg text-xs font-medium
                  transition-all flex items-center gap-1
                  ${isSelected 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-white text-blue-700 hover:bg-blue-100'
                  }
                `}
              >
                <Icon className="w-3 h-3" />
                {action.name}
              </motion.button>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        Personalize sua experiência
      </h3>

      {/* Tema */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <Palette className="w-4 h-4" />
            Tema da aplicação
          </div>
        </label>
        
        <div className="grid grid-cols-3 gap-3">
          {THEMES.map((theme) => {
            const Icon = theme.icon;
            const isSelected = preferences.theme === theme.id;
            
            return (
              <motion.button
                key={theme.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setPreferences(prev => ({ ...prev, theme: theme.id }))}
                className={`
                  p-4 rounded-xl border-2 text-center
                  transition-all relative
                  ${isSelected 
                    ? 'border-blue-500 bg-gradient-to-br ' + theme.color + ' bg-opacity-10' 
                    : 'border-slate-200 hover:border-slate-300'
                  }
                `}
              >
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center"
                  >
                    <Check className="w-3 h-3 text-white" />
                  </motion.div>
                )}
                
                <div className={`${theme.bgColor} w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 text-${theme.id === 'dark' ? 'indigo' : theme.id === 'auto' ? 'emerald' : 'amber'}-600`} />
                </div>
                
                <p className="text-sm font-medium text-slate-900">{theme.name}</p>
                <p className="text-xs text-slate-500 mt-1">{theme.description}</p>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Notificações */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4" />
            Notificações
          </div>
        </label>
        
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
          <div>
            <p className="font-medium text-slate-900">Receber notificações</p>
            <p className="text-xs text-slate-500">Alertas de tarefas, lembretes e atualizações</p>
          </div>
          
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setPreferences(prev => ({ ...prev, notifications: !prev.notifications }))}
            className={`
              w-12 h-6 rounded-full transition-all
              ${preferences.notifications ? 'bg-blue-600' : 'bg-slate-300'}
            `}
          >
            <motion.div
              animate={{ x: preferences.notifications ? 24 : 2 }}
              className="w-5 h-5 bg-white rounded-full shadow-md"
            />
          </motion.button>
        </div>
      </div>

      {/* Preview do tema */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mt-4 p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl"
      >
        <p className="text-xs font-medium text-slate-500 mb-2">Preview</p>
        <div className={`p-3 rounded-lg ${
          preferences.theme === 'dark' ? 'bg-slate-800' : 
          preferences.theme === 'light' ? 'bg-white' : 'bg-gradient-to-br from-white to-slate-50'
        } border border-slate-200`}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-lg" />
            <div>
              <div className={`w-24 h-2 rounded-full mb-1 ${
                preferences.theme === 'dark' ? 'bg-slate-600' : 'bg-slate-200'
              }`} />
              <div className={`w-16 h-2 rounded-full ${
                preferences.theme === 'dark' ? 'bg-slate-700' : 'bg-slate-300'
              }`} />
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      key="step3"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        Ative os módulos desejados
      </h3>
      
      <p className="text-sm text-slate-600 mb-4">
        Você pode ativar ou desativar módulos a qualquer momento
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {MODULES.map((module, index) => {
          const Icon = module.icon;
          const isSelected = preferences.modules.includes(module.id);
          
          return (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className={`
                relative p-5 rounded-xl border-2 cursor-pointer
                transition-all
                ${isSelected 
                  ? 'border-blue-500 bg-gradient-to-br ' + module.bgColor 
                  : 'border-slate-200 hover:border-slate-300'
                }
              `}
              onClick={() => handleModuleToggle(module.id)}
            >
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-3 right-3"
                >
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                </motion.div>
              )}
              
              <div className="flex items-start gap-3">
                <div className={`p-3 rounded-lg bg-gradient-to-br ${module.color}`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">
                    {module.name}
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">
                    {module.description}
                  </p>
                  
                  <ul className="space-y-1">
                    {module.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-1 text-xs text-slate-600">
                        <Check className="w-3 h-3 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Resumo da seleção */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mt-4 p-4 bg-blue-50 rounded-xl"
      >
        <p className="text-sm font-medium text-blue-900 mb-2">
          {preferences.modules.length} módulo(s) selecionado(s)
        </p>
        <div className="flex flex-wrap gap-2">
          {preferences.modules.map(moduleId => {
            const module = MODULES.find(m => m.id === moduleId);
            if (!module) return null;
            const Icon = module.icon;
            
            return (
              <span
                key={moduleId}
                className="px-2 py-1 bg-white rounded-lg text-xs flex items-center gap-1"
              >
                <Icon className="w-3 h-3" />
                {module.name}
              </span>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );

  const renderStep4 = () => (
    <motion.div
      key="step4"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8 text-center"
    >
      {/* Ícone de conclusão */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200 }}
        className="relative"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full blur-xl opacity-30" />
        <div className="relative w-32 h-32 mx-auto bg-gradient-to-br from-emerald-500 to-green-600 rounded-3xl flex items-center justify-center shadow-2xl">
          <CreditCard className="w-16 h-16 text-white" />
        </div>
      </motion.div>

      {/* Mensagem */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h2 className="text-2xl font-bold text-slate-900 mb-3">
          Escolha seu plano
        </h2>
        <p className="text-slate-600 mb-2">
          Sua conta está configurada!
        </p>
        <p className="text-sm text-slate-500 mb-6">
          Agora escolha um plano para começar com <strong className="text-emerald-600">7 dias grátis</strong>.
          Cancele quando quiser.
        </p>
      </motion.div>

      {/* Resumo da configuração */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-slate-50 rounded-xl p-6 text-left"
      >
        <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-500" />
          Sua configuração:
        </h3>
        
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2">
            <div className="w-1 h-1 bg-blue-500 rounded-full" />
            <span className="text-slate-600">Tema: <strong className="text-slate-900">
              {THEMES.find(t => t.id === preferences.theme)?.name}
            </strong></span>
          </li>
          <li className="flex items-center gap-2">
            <div className="w-1 h-1 bg-blue-500 rounded-full" />
            <span className="text-slate-600">Notificações: <strong className="text-slate-900">
              {preferences.notifications ? 'Ativadas' : 'Desativadas'}
            </strong></span>
          </li>
          <li className="flex items-center gap-2">
            <div className="w-1 h-1 bg-blue-500 rounded-full" />
            <span className="text-slate-600">Módulos ativos: <strong className="text-slate-900">
              {preferences.modules.length}
            </strong></span>
          </li>
          <li className="flex items-center gap-2">
            <div className="w-1 h-1 bg-emerald-500 rounded-full" />
            <span className="text-slate-600">Plano: <strong className="text-emerald-600">
              7 dias grátis · Cancele quando quiser
            </strong></span>
          </li>
        </ul>
      </motion.div>

      {/* Confetti overlay */}
      <AnimatePresence>
        {showConfetti && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 pointer-events-none z-50"
          >
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: -20,
                  rotate: 0
                }}
                animate={{
                  y: window.innerHeight + 100,
                  rotate: 360 * (Math.random() > 0.5 ? 1 : -1)
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  delay: Math.random() * 0.5,
                  ease: 'easeOut'
                }}
                className="absolute"
              >
                <div className={`
                  w-4 h-4 rounded-sm
                  ${i % 3 === 0 ? 'bg-emerald-500' : i % 3 === 1 ? 'bg-blue-500' : 'bg-purple-500'}
                `} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  // ============================================
  // 🎨 RENDER PRINCIPAL
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = completedSteps.has(step.id);
              
              return (
                <div key={step.id} className="flex-1 last:flex-none">
                  <div className="flex items-center">
                    <motion.div
                      animate={{
                        scale: isActive ? 1.1 : 1,
                        backgroundColor: isCompleted ? '#10b981' : isActive ? '#3b82f6' : '#e2e8f0'
                      }}
                      className={`
                        w-10 h-10 rounded-full flex items-center justify-center
                        transition-colors relative
                      `}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5 text-white" />
                      ) : (
                        <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                      )}
                      
                      {/* Tooltip */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 10 }}
                        className="absolute -bottom-12 left-1/2 -translate-x-1/2 whitespace-nowrap"
                      >
                        <div className="bg-slate-900 text-white text-xs py-1 px-2 rounded-lg">
                          {step.name}
                        </div>
                      </motion.div>
                    </motion.div>
                    
                    {step.id < 4 && (
                      <div className={`flex-1 h-0.5 mx-2 ${
                        isCompleted ? 'bg-green-500' : 'bg-slate-200'
                      }`} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="text-center">
            <p className="text-sm text-slate-500">
              Passo {currentStep} de 4 • {STEPS[currentStep - 1].description}
            </p>
          </div>
        </div>

        {/* Content */}
        <motion.div
          layout
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-slate-100"
        >
          <AnimatePresence mode="wait">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
          </AnimatePresence>

          {/* Navigation */}
          <motion.div 
            layout
            className="flex justify-between mt-8 pt-6 border-t border-slate-100"
          >
            <div>
              {currentStep > 1 && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handlePrevious}
                  className="px-6 py-2.5 border-2 border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-all flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </motion.button>
              )}
            </div>
            
            <div className="flex gap-3">
              {currentStep < 4 && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onSkip}
                  className="px-6 py-2.5 text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Pular
                </motion.button>
              )}
              
              {currentStep < 4 ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleNext}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl"
                >
                  Continuar
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleComplete}
                  disabled={loading}
                  className={`
                    px-8 py-2.5 rounded-xl font-medium
                    transition-all flex items-center gap-2
                    ${loading
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-emerald-600 to-green-600 text-white hover:from-emerald-700 hover:to-green-700 shadow-lg hover:shadow-xl'
                    }
                  `}
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      />
                      Redirecionando...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Ver planos disponíveis
                    </>
                  )}
                </motion.button>
              )}
            </div>
          </motion.div>
        </motion.div>

        {/* Skip option */}
        {currentStep === 1 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-4 text-xs text-slate-400"
          >
            Você pode configurar tudo isso depois nas configurações
          </motion.p>
        )}
      </div>
    </div>
  );
};

// ✅ Otimização: memo para evitar re-renders desnecessários
export default React.memo(Onboarding);